/**
 * Cálculo financiero puro.
 *
 * Vive en el frontend a propósito: el simulador da feedback instantáneo sin
 * round-trip y al backend solo le llega el evento `simulator.change` con los
 * inputs. Sin React, sin DOM: todo son funciones puras y testeables.
 *
 * Convenio de tasas: **internamente todo es decimal** (12 % → 0.12). La
 * conversión desde los inputs ocurre en un solo sitio (`resolveRate`).
 */

import { formatAccounting, formatDecimal, formatPercent } from "../lib/format";

export type SimulatorKind = "npv" | "irr" | "wacc" | "working_capital" | "compound_interest";

export interface CalculatorRow {
  label: string;
  value: string;
  hint?: string | null;
  /** Fila de cierre (total/subtotal): se pinta destacada. */
  emphasis?: boolean;
}

export interface CalculatorPrimary {
  label: string;
  value: number;
  /** Valor ya formateado para pintar. */
  formatted: string;
  unit?: string | null;
  /** Lectura de la decisión: "Conviene ejecutar", "No conviene", etc. */
  verdict?: string | null;
  /** Dirección del veredicto, para el color y el icono. */
  tone?: "positive" | "negative" | "neutral";
}

export interface CalculatorResult {
  primary: CalculatorPrimary | null;
  rows: CalculatorRow[];
  note?: string | null;
  error?: string | null;
  /** Perfil de VAN (tasa → valor), para dibujar la sensibilidad. */
  profile?: { rates: number[]; values: number[] } | null;
}

export type SimulatorInputs = Record<string, number>;

/* -------------------------------------------------------------------------- */
/* Primitivas                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Valor actual neto. `cashFlows[0]` ocurre en t = 0 (la inversión, en
 * negativo) y el resto en t = 1..n. `rate` va en decimal.
 */
export function netPresentValue(rate: number, cashFlows: readonly number[]): number {
  let total = 0;
  for (let period = 0; period < cashFlows.length; period += 1) {
    const flow = cashFlows[period];
    if (flow === undefined) continue;
    total += presentValue(flow, rate, period);
  }
  return total;
}

/** Derivada numérica del VAN respecto de la tasa (diferencia central). */
function npvDerivative(rate: number, cashFlows: readonly number[]): number {
  const h = 1e-6;
  return (netPresentValue(rate + h, cashFlows) - netPresentValue(rate - h, cashFlows)) / (2 * h);
}

export interface IrrOptions {
  /** Tasa inicial del método de Newton. */
  guess?: number;
  /** Cota inferior del intervalo de bisección. */
  lower?: number;
  /** Cota superior del intervalo de bisección. */
  upper?: number;
  /** Tolerancia sobre el VAN. */
  tolerance?: number;
  /** Iteraciones máximas por método. */
  maxIterations?: number;
}

/**
 * Tasa interna de retorno.
 *
 * Primero Newton-Raphson (converge en pocas iteraciones cuando la función es
 * suave), y si diverge, se cae a bisección sobre un intervalo amplio, que es
 * más lento pero no falla si hay cambio de signo. Devuelve `null` cuando no
 * existe una TIR razonable — por ejemplo si todos los flujos son del mismo
 * signo, que es justo el caso que hay que saber detectar antes de decidir.
 */
export function internalRateOfReturn(
  cashFlows: readonly number[],
  options: IrrOptions = {},
): number | null {
  const {
    guess = 0.1,
    lower = -0.9999,
    upper = 10,
    tolerance = 1e-9,
    maxIterations = 80,
  } = options;

  if (cashFlows.length < 2) return null;
  const hasPositive = cashFlows.some((flow) => flow > 0);
  const hasNegative = cashFlows.some((flow) => flow < 0);
  if (!hasPositive || !hasNegative) return null;

  // --- Newton ---
  let rate = guess;
  for (let iteration = 0; iteration < maxIterations; iteration += 1) {
    const value = netPresentValue(rate, cashFlows);
    if (!Number.isFinite(value)) break;
    if (Math.abs(value) < tolerance) return rate;
    const slope = npvDerivative(rate, cashFlows);
    if (!Number.isFinite(slope) || Math.abs(slope) < 1e-12) break;
    const next = rate - value / slope;
    if (!Number.isFinite(next) || next <= lower) break;
    if (Math.abs(next - rate) < tolerance) {
      return Math.abs(netPresentValue(next, cashFlows)) < 1e-6 ? next : null;
    }
    rate = next;
  }

  // --- Bisección (respaldo) ---
  let low = lower;
  let high = upper;
  let valueLow = netPresentValue(low, cashFlows);
  let valueHigh = netPresentValue(high, cashFlows);
  if (!Number.isFinite(valueLow) || !Number.isFinite(valueHigh)) return null;
  if (valueLow * valueHigh > 0) return null;

  for (let iteration = 0; iteration < 200; iteration += 1) {
    const middle = (low + high) / 2;
    const valueMiddle = netPresentValue(middle, cashFlows);
    if (Math.abs(valueMiddle) < 1e-7 || high - low < 1e-12) return middle;
    if (valueLow * valueMiddle < 0) {
      high = middle;
      valueHigh = valueMiddle;
    } else {
      low = middle;
      valueLow = valueMiddle;
    }
  }
  return (low + high) / 2;
}

/** Perfil de VAN: la curva que muestra dónde cruza cero la TIR. */
export function npvProfile(
  cashFlows: readonly number[],
  options: { from?: number; to?: number; steps?: number } = {},
): { rates: number[]; values: number[] } {
  const { from = 0, to = 0.6, steps = 24 } = options;
  const rates: number[] = [];
  const values: number[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const rate = from + ((to - from) * index) / steps;
    rates.push(Number(rate.toFixed(4)));
    values.push(Number(netPresentValue(rate, cashFlows).toFixed(2)));
  }
  return { rates, values };
}

export interface WaccInputs {
  equity: number;
  debt: number;
  costOfEquity: number;
  costOfDebt: number;
  taxRate: number;
}

/** WACC = (E/V)·Ke + (D/V)·Kd·(1 − T). Tasas en decimal. */
export function weightedAverageCostOfCapital(inputs: WaccInputs): number | null {
  const { equity, debt, costOfEquity, costOfDebt, taxRate } = inputs;
  const total = equity + debt;
  if (total <= 0) return null;
  return (equity / total) * costOfEquity + (debt / total) * costOfDebt * (1 - taxRate);
}

export interface WorkingCapitalInputs {
  receivables: number;
  inventory: number;
  payables: number;
}

/** CTN = Cuentas por cobrar + Inventarios − Cuentas por pagar. */
export function workingCapital(inputs: WorkingCapitalInputs): number {
  return inputs.receivables + inputs.inventory - inputs.payables;
}

export interface CompoundInterestInputs {
  principal: number;
  /** Tasa nominal anual, en decimal. */
  rate: number;
  years: number;
  /** Capitalizaciones por año (1 anual, 12 mensual, 4 trimestral…). */
  compoundsPerYear: number;
}

export interface CompoundInterestResult {
  futureValue: number;
  /** Tasa efectiva anual equivalente. */
  effectiveAnnualRate: number;
  /** Interés total ganado. */
  interest: number;
}

/** VF = VP·(1 + i/m)^(n·m) */
export function compoundInterest(inputs: CompoundInterestInputs): CompoundInterestResult {
  const { principal, rate, years, compoundsPerYear } = inputs;
  const periods = Math.max(1, compoundsPerYear);
  const periodicRate = rate / periods;
  const totalPeriods = periods * Math.max(0, years);
  const result = futureValue(principal, periodicRate, totalPeriods);
  return {
    futureValue: result,
    effectiveAnnualRate: (1 + periodicRate) ** periods - 1,
    interest: result - principal,
  };
}

/** VA = VF / (1 + i)^n */
export function presentValue(futureValueAmount: number, rate: number, periods: number): number {
  return futureValueAmount / (1 + rate) ** periods;
}

/** VF = VA·(1 + i)^n */
export function futureValue(presentValueAmount: number, rate: number, periods: number): number {
  return presentValueAmount * (1 + rate) ** periods;
}

/**
 * Payback descontado: periodo en que el acumulado cruza cero (interpolado).
 */
export function discountedPayback(rate: number, cashFlows: readonly number[]): number | null {
  let accumulated = 0;
  for (let period = 0; period < cashFlows.length; period += 1) {
    const flow = cashFlows[period] ?? 0;
    const discounted = presentValue(flow, rate, period);
    const previous = accumulated;
    accumulated += discounted;
    if (period > 0 && previous < 0 && accumulated >= 0) {
      const fraction = discounted === 0 ? 0 : -previous / discounted;
      return period - 1 + fraction;
    }
  }
  return null;
}

/* -------------------------------------------------------------------------- */
/* Resolución de inputs                                                       */
/* -------------------------------------------------------------------------- */

/**
 * El backend declara las claves de los inputs y aquí se resuelven por lista de
 * sinónimos. Es deliberadamente tolerante: si mañana el servidor renombra
 * `ke` a `cost_of_equity`, el simulador sigue funcionando sin tocar la UI.
 */
function pick(inputs: SimulatorInputs, aliases: readonly string[], fallback: number): number {
  for (const alias of aliases) {
    const value = inputs[alias];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  // Segundo paso: comparación normalizada (sin mayúsculas ni separadores).
  const normalized = new Map<string, number>();
  for (const [key, value] of Object.entries(inputs)) {
    if (typeof value === "number" && Number.isFinite(value)) {
      normalized.set(key.toLowerCase().replace(/[^a-z0-9]/g, ""), value);
    }
  }
  for (const alias of aliases) {
    const value = normalized.get(alias.toLowerCase().replace(/[^a-z0-9]/g, ""));
    if (typeof value === "number") return value;
  }
  return fallback;
}

function has(inputs: SimulatorInputs, aliases: readonly string[]): boolean {
  const keys = Object.keys(inputs).map((key) => key.toLowerCase().replace(/[^a-z0-9]/g, ""));
  return aliases.some((alias) =>
    keys.includes(alias.toLowerCase().replace(/[^a-z0-9]/g, "")),
  );
}

export const RATE_ALIASES = {
  rate: ["rate", "discount_rate", "tasa", "tasa_descuento", "i", "wacc", "tasa_descuento_anual"],
  equity: ["equity", "e", "patrimonio", "capital_propio"],
  debt: ["debt", "d", "deuda", "pasivo_financiero"],
  costOfEquity: ["ke", "cost_of_equity", "costo_patrimonio", "costo_capital_propio"],
  costOfDebt: ["kd", "cost_of_debt", "costo_deuda"],
  tax: ["tax", "t", "tax_rate", "impuesto", "tasa_impuesto"],
  investment: ["investment", "initial_investment", "i0", "inversion_inicial", "inversion"],
  receivables: ["receivables", "accounts_receivable", "cuentas_por_cobrar", "cxc", "cxcobrar"],
  inventory: ["inventory", "inventories", "inventario", "inventarios"],
  payables: ["payables", "accounts_payable", "cuentas_por_pagar", "cxp", "cxpagar"],
  principal: ["principal", "present_value", "va", "vp", "capital", "monto"],
  years: ["years", "n", "periods", "periodos", "plazo", "anios", "tiempo"],
  frequency: ["frequency", "m", "compounds_per_year", "capitalizaciones", "frecuencia"],
} as const;

function resolveRate(
  inputs: SimulatorInputs,
  aliases: readonly string[],
  fallback: number,
): number {
  if (!has(inputs, aliases)) return fallback;
  const raw = pick(inputs, aliases, fallback);
  // Un valor ≤ 1 se lee como decimal; por encima, como puntos porcentuales.
  return Math.abs(raw) <= 1 ? raw : raw / 100;
}

/** Construye la serie de flujos: t=0 es la inversión cuando se declara aparte. */
export function resolveCashFlows(
  inputs: SimulatorInputs,
  declared: readonly number[] | undefined,
): number[] {
  const flows = (declared ?? []).filter((flow) => Number.isFinite(flow));
  if (has(inputs, RATE_ALIASES.investment)) {
    const investment = pick(inputs, RATE_ALIASES.investment, 0);
    // Si ya viene un t=0 negativo en la serie, la inversión declarada manda.
    const start = flows.length > 0 && flows[0] !== undefined && flows[0] < 0 ? flows.slice(1) : flows;
    return [-Math.abs(investment), ...start];
  }
  return flows.slice();
}

/* -------------------------------------------------------------------------- */
/* Dispatcher                                                                 */
/* -------------------------------------------------------------------------- */

function money(value: number): string {
  return formatAccounting(value, null, { decimals: 0 });
}

export function computeSimulator(
  kind: SimulatorKind,
  inputs: SimulatorInputs,
  declaredCashFlows?: readonly number[],
): CalculatorResult {
  switch (kind) {
    case "npv": {
      const rate = resolveRate(inputs, RATE_ALIASES.rate, 0.1);
      const flows = resolveCashFlows(inputs, declaredCashFlows);
      if (flows.length < 2) {
        return {
          primary: null,
          rows: [],
          error: "Faltan los flujos de caja del proyecto para calcular el VAN.",
        };
      }
      const value = netPresentValue(rate, flows);
      const irr = internalRateOfReturn(flows);
      const payback = discountedPayback(rate, flows);
      const discounted = flows.reduce(
        (total, flow, period) => total + flow / (1 + rate) ** period,
        0,
      );
      const invested = Math.abs(flows[0] ?? 0);

      return {
        primary: {
          label: "VAN",
          value,
          formatted: money(value),
          unit: "S/",
          verdict:
            value > 0
              ? "El proyecto crea valor a esta tasa."
              : value < 0
                ? "El proyecto destruye valor a esta tasa."
                : "El proyecto es indiferente a esta tasa.",
          tone: value > 0 ? "positive" : value < 0 ? "negative" : "neutral",
        },
        rows: [
          { label: "Inversión inicial", value: money(-invested) },
          { label: "Flujos descontados", value: money(discounted), hint: `a ${formatPercent(rate)}` },
          { label: "VAN", value: money(value), emphasis: true },
          {
            label: "TIR",
            value: irr === null ? "No calculable" : formatPercent(irr),
            hint: irr === null ? "Los flujos no cambian de signo" : `vs. tasa ${formatPercent(rate)}`,
          },
          {
            label: "Payback descontado",
            value: payback === null ? "No se recupera" : `${formatDecimal(payback)} años`,
          },
        ],
        profile: npvProfile(flows, { from: 0, to: Math.max(0.6, rate * 3), steps: 24 }),
      };
    }

    case "irr": {
      const flows = resolveCashFlows(inputs, declaredCashFlows);
      if (flows.length < 2) {
        return {
          primary: null,
          rows: [],
          error: "Faltan los flujos de caja del proyecto para calcular la TIR.",
        };
      }
      const irr = internalRateOfReturn(flows);
      const rate = resolveRate(inputs, RATE_ALIASES.rate, 0.1);
      const invested = Math.abs(flows[0] ?? 0);
      return {
        primary: irr === null
          ? {
              label: "TIR",
              value: Number.NaN,
              formatted: "No calculable",
              verdict: "Los flujos no cambian de signo: no hay tasa que anule el VAN.",
              tone: "neutral",
            }
          : {
              label: "TIR",
              value: irr,
              formatted: formatPercent(irr),
              verdict:
                irr > rate
                  ? `Supera la tasa de descuento (${formatPercent(rate)}).`
                  : `No supera la tasa de descuento (${formatPercent(rate)}).`,
              tone: irr > rate ? "positive" : "negative",
            },
        rows: [
          { label: "Inversión inicial", value: money(-invested) },
          { label: "Tasa de descuento", value: formatPercent(rate) },
          { label: "TIR", value: irr === null ? "No calculable" : formatPercent(irr), emphasis: true },
          irr === null
            ? { label: "VAN a la tasa de descuento", value: money(netPresentValue(rate, flows)) }
            : { label: "VAN a la TIR", value: money(netPresentValue(irr, flows)), hint: "debe ser ≈ 0" },
        ],
        profile: npvProfile(flows, { from: 0, to: Math.max(0.6, rate * 3), steps: 24 }),
      };
    }

    case "wacc": {
      const equity = pick(inputs, RATE_ALIASES.equity, 0);
      const debt = pick(inputs, RATE_ALIASES.debt, 0);
      const costOfEquity = resolveRate(inputs, RATE_ALIASES.costOfEquity, 0);
      const costOfDebt = resolveRate(inputs, RATE_ALIASES.costOfDebt, 0);
      const taxRate = resolveRate(inputs, RATE_ALIASES.tax, 0);
      const total = equity + debt;
      const wacc = weightedAverageCostOfCapital({
        equity,
        debt,
        costOfEquity,
        costOfDebt,
        taxRate,
      });
      if (wacc === null) {
        return { primary: null, rows: [], error: "El capital total (E + D) debe ser mayor que cero." };
      }
      const equityShare = total === 0 ? 0 : equity / total;
      const debtShare = total === 0 ? 0 : debt / total;
      return {
        primary: {
          label: "WACC",
          value: wacc,
          formatted: formatPercent(wacc),
          verdict: `Costo promedio ponderado de capital con ${formatPercent(equityShare)} de patrimonio.`,
          tone: "neutral",
        },
        rows: [
          { label: "Patrimonio (E)", value: money(equity) },
          { label: "Deuda (D)", value: money(debt) },
          { label: "Capital total (V)", value: money(total), emphasis: true },
          {
            label: "E/V · Ke",
            value: formatPercent(equityShare * costOfEquity),
            hint: `${formatPercent(equityShare)} × ${formatPercent(costOfEquity)}`,
          },
          {
            label: "D/V · Kd · (1 − T)",
            value: formatPercent(debtShare * costOfDebt * (1 - taxRate)),
            hint: `${formatPercent(debtShare)} × ${formatPercent(costOfDebt)} × (1 − ${formatPercent(taxRate)})`,
          },
          { label: "WACC", value: formatPercent(wacc), emphasis: true },
        ],
      };
    }

    case "working_capital": {
      const receivables = pick(inputs, RATE_ALIASES.receivables, 0);
      const inventory = pick(inputs, RATE_ALIASES.inventory, 0);
      const payables = pick(inputs, RATE_ALIASES.payables, 0);
      const ctn = workingCapital({ receivables, inventory, payables });
      return {
        primary: {
          label: "Capital de trabajo neto",
          value: ctn,
          formatted: money(ctn),
          unit: "S/",
          verdict:
            ctn >= 0
              ? "El circulante se financia con recursos de corto plazo."
              : "Las fuentes de corto plazo no cubren el circulante: revisa el fondo de maniobra.",
          tone: ctn >= 0 ? "positive" : "negative",
        },
        rows: [
          { label: "Cuentas por cobrar", value: money(receivables), hint: "suma" },
          { label: "Inventarios", value: money(inventory), hint: "suma" },
          { label: "Cuentas por pagar", value: money(payables), hint: "resta" },
          { label: "Capital de trabajo neto", value: money(ctn), emphasis: true },
        ],
        note: "CTN = Cuentas por cobrar + Inventarios − Cuentas por pagar.",
      };
    }

    case "compound_interest": {
      const principal = pick(inputs, RATE_ALIASES.principal, 0);
      const rate = resolveRate(inputs, RATE_ALIASES.rate, 0);
      const years = pick(inputs, RATE_ALIASES.years, 1);
      const compoundsPerYear = pick(inputs, RATE_ALIASES.frequency, 1);
      const result = compoundInterest({ principal, rate, years, compoundsPerYear });
      const periods = years * compoundsPerYear;
      return {
        primary: {
          label: "Valor futuro",
          value: result.futureValue,
          formatted: money(result.futureValue),
          unit: "S/",
          verdict: `Con ${formatDecimal(periods)} periodos de capitalización.`,
          tone: "neutral",
        },
        rows: [
          { label: "Valor presente (VP)", value: money(principal) },
          { label: "Tasa nominal anual", value: formatPercent(rate) },
          { label: "Periodos", value: formatDecimal(periods), hint: `${formatDecimal(years)} años × ${formatDecimal(compoundsPerYear)}` },
          { label: "Interés ganado", value: money(result.interest) },
          { label: "Tasa efectiva anual", value: formatPercent(result.effectiveAnnualRate) },
          { label: "Valor futuro (VF)", value: money(result.futureValue), emphasis: true },
        ],
        note: "VF = VP·(1 + i/m)^(n·m). Con m = 1 se recupera la capitalización anual.",
      };
    }

    default:
      return { primary: null, rows: [], error: "Tipo de simulador no soportado." };
  }
}

/** Etiqueta legible de cada tipo de simulador. */
export const SIMULATOR_LABELS: Record<SimulatorKind, string> = {
  npv: "Valor actual neto",
  irr: "Tasa interna de retorno",
  wacc: "Costo promedio ponderado de capital",
  working_capital: "Capital de trabajo neto",
  compound_interest: "Interés compuesto",
};
